import { StorageService } from './storageService';
import { CreateUserInput, LoginInput, UserResponse } from '../../models/core/user';
import { CreateStudentInput } from '../../models/core/student';

export class AuthService {
  // ============ REGISTRATION ============
  
  static async register(userData: CreateUserInput, studentData?: Partial<CreateStudentInput>): Promise<{
    user: UserResponse;
    student?: any;
  }> {
    const user = await StorageService.create("users", userData);
    let student = null;
    
    if (studentData) {
      student = await StorageService.create("students", { ...studentData, userId: user._id });
    }

    return {
      user: user as UserResponse,
      student,
    };
  }

  // ============ LOGIN ============
  
  static async login(credentials: LoginInput): Promise<UserResponse> {
    // Replaced 'authenticateUser' with a generic 'search'
    const users = await StorageService.search("users", { 
      email: credentials.email, 
      password: credentials.password 
    });
    
    const user = users[0];
    if (!user) {
      throw new Error('Invalid email or password');
    }

    StorageService.write("currentUser", user); // Replaced 'setCurrentUser'
    return user as UserResponse;
  }

  // ============ LOGOUT ============
  
  static logout(): void {
    StorageService.delete("currentUser");
  }

  // ============ SESSION MANAGEMENT ============
  
  static getCurrentUser(): UserResponse | null {
    const userStr = StorageService.find("currentUser");
    return userStr ? JSON.parse(userStr) as UserResponse : null;
  }

  static isAuthenticated(): boolean {
    return !!StorageService.find("currentUser");
  }

  // ============ PASSWORD RESET ============
  
  static async requestPasswordReset(email: string): Promise<void> {
    const user = await StorageService.get("users", email);
    if (!user) return;

    const resetToken = `reset_${Date.now()}_${Math.random().toString(36).substr(2)}`;
    const resetExpires = new Date(Date.now() + 1 * 60 * 60 * 1000);

    await StorageService.update("users", user._id, {
      passwordResetToken: resetToken,
      passwordResetExpires: resetExpires,
    });

    console.log(`Password reset token for ${email}: ${resetToken}`);
  }

  static async resetPassword(token: string, newPassword: string): Promise<void> {
    const users = StorageService.read("users");
    const user = users.find((u: any) => 
      u.passwordResetToken === token && 
      u.passwordResetExpires && 
      new Date() < new Date(u.passwordResetExpires)
    );

    if (!user) {
      throw new Error('Invalid or expired reset token');
    }

    await StorageService.update("users", user._id, {
      password: newPassword,
      passwordResetToken: undefined,
      passwordResetExpires: undefined,
    });
  }

  // ============ PROFILE MANAGEMENT ============
  
  static async updateProfile(userId: string, updates: Partial<UserResponse>): Promise<UserResponse> {
    const updatedUser = await StorageService.update("users", userId, updates);
    StorageService.write("currentUser", updatedUser);
    return updatedUser as UserResponse;
  }

  static async changePassword(userId: string, currentPassword: string, newPassword: string): Promise<void> {
    const user = await StorageService.get("users", userId);
    
    if (!user) {
      throw new Error('User not found');
    }

    if (user.password !== currentPassword) {
      throw new Error('Current password is incorrect');
    }

    await StorageService.update("users", userId, {
      password: newPassword,
    });
  }
}